import React from 'react';
import { Box, Typography, Container, Grid, Divider, Link, useTheme, alpha } from '@mui/material';
import { 
  GitHub as GitHubIcon, 
  Dns as ServerIcon, 
  Storage as StorageIcon, 
  Code as CodeIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const Footer = ({ onNavigate }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  const techStack = [
    { label: 'Backend', value: 'FastAPI (Python)', icon: '/fastapi.png', iconType: 'image' },
    { label: 'Frontend', value: 'React 18 + MUI v5', icon: '/reactjs.png', iconType: 'image' },
    { label: 'Database', value: 'SQLite (async)', icon: '/sqlitebrowser.png', iconType: 'image' },
    { label: 'Infrastructure', value: 'Docker + Nginx', icon: '/docker-moby.png', iconType: 'image' },
  ];

  const deploymentDetails = [
    { label: 'WatchTower', value: 'Raspberry Pi 5', icon: '/raspberry-pi.png', iconType: 'image' },
    { label: 'Home Assistant', value: 'Raspberry Pi 5', icon: '/raspberry-pi.png', iconType: 'image' },
    { label: 'Storage Server', value: 'TrueNAS Scale', icon: '/truenas_logo.png', iconType: 'image' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: 4,
        px: 2,
        background: 'rgba(7, 11, 22, 0.4)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4} justifyContent="space-between">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/favicon_watchtower/WT_TransparentBG.png"
                alt="WatchTower"
                sx={{ width: 32, height: 32, borderRadius: '4px' }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Orbitron', sans-serif",
                  fontWeight: 900,
                  fontSize: '1rem',
                  letterSpacing: '2px',
                  background: 'linear-gradient(135deg, #00d4ff 0%, #ffffff 50%, #00d4ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                WatchTower
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2, lineHeight: 1.6 }}>
              {t('footer.description')}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {t('footer.techStack')}
            </Typography>
            {techStack.map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {item.iconType === 'image' ? (
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.label}
                    sx={{ width: 20, height: 20, objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{item.icon}</Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', lineHeight: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {t('footer.deploymentDetails')}
            </Typography>
            {deploymentDetails.map((item) => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {item.iconType === 'image' ? (
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.label}
                    sx={{ width: 24, height: 24, objectFit: 'contain' }}
                  />
                ) : (
                  <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>{item.icon}</Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', lineHeight: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                    {item.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.05)' }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            {t('footer.copyright')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link 
              component="button"
              onClick={() => onNavigate('documentation')}
              sx={{ color: 'rgba(255, 255, 255, 0.3)', textDecoration: 'none', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', p: 0, '&:hover': { color: '#00d4ff' } }}
            >
              {t('footer.documentation')}
            </Link>
            <Link href="#" sx={{ color: 'rgba(255, 255, 255, 0.3)', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: '#00d4ff' } }}>
              {t('footer.apiStatus')}
            </Link>
            <Link href="#" sx={{ color: 'rgba(255, 255, 255, 0.3)', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: '#00d4ff' } }}>
              {t('footer.security')}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
